import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  MapPin, Users, CheckCircle, Clock, Calendar, Search,
  Store, Plus, AlertCircle, Target, MoreVertical, XCircle, Trash2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import ConfirmModal from '../../components/modals/ConfirmModal';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'sonner';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const VisitsTasksPage = () => {
  const { activeBusiness } = useBusiness();
  const { businessId } = useParams();
  const [loading, setLoading] = useState(true);
  const [visits, setVisits] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [activeTab, setActiveTab] = useState('visits');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [parties, setParties] = useState([]);
  const [confirmModal, setConfirmModal] = useState(null);
  const [visitForm, setVisitForm] = useState({
    staff_user_id: '',
    party_id: '',
    purpose: 'ORDER',
    priority: 'NORMAL',
    scheduled_date: new Date().toISOString().split('T')[0],
    scheduled_from_time: '',
    scheduled_to_time: '',
    notes: '',
  });

  const [taskForm, setTaskForm] = useState({
    staff_id: '',
    title: '',
    description: '',
    due_date: new Date().toISOString().split('T')[0],
    priority: 'NORMAL',
  });

  useEffect(() => {
    fetchVisits();
    fetchTasks();
  }, [selectedDate, selectedStaff]);

  useEffect(() => {
    fetchStaffList();
    fetchParties();
  }, []);

  const fetchVisits = async () => {
    try {
      const params = { date: selectedDate };
      if (selectedStaff) params.staff_id = selectedStaff;
      const { data } = await api.get('/part3/visits', { params });
      setVisits(Array.isArray(data) ? data : (data.visits || []));
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load visits'));
    }
  };

  const fetchTasks = async () => {
    try {
      const { data } = await api.get('/part3/tasks');
      setTasks(Array.isArray(data) ? data : (data.tasks || []));
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load tasks'));
    }
  };

  const fetchStaffList = async () => {
    try {
      const { data } = await api.get('/part3/staff');
      setStaffList(data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load staff'));
    }
  };

  const fetchParties = async () => {
    try {
      // Fetch all active parties (not just customers) — staff visits any party type
      const { data } = await api.get('/parties', { params: { is_active: true } });
      setParties(data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load parties'));
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await api.post('/part3/tasks', taskForm);
      toast.success('Task created successfully!');
      setShowTaskModal(false);
      setTaskForm({
        staff_id: '',
        title: '',
        description: '',
        due_date: new Date().toISOString().split('T')[0],
        priority: 'NORMAL',
      });
      fetchTasks();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to create task'));
    }
  };

  const handleCreateVisit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        staff_user_id: visitForm.staff_user_id,
        party_id: visitForm.party_id,
        purpose: visitForm.purpose,
        priority: visitForm.priority,
      };
      if (visitForm.scheduled_date) payload.scheduled_date = visitForm.scheduled_date + 'T00:00:00Z';
      if (visitForm.scheduled_from_time) payload.scheduled_from_time = visitForm.scheduled_from_time;
      if (visitForm.scheduled_to_time) payload.scheduled_to_time = visitForm.scheduled_to_time;
      if (visitForm.notes) payload.notes = visitForm.notes;
      await api.post('/part3/visits', payload);
      toast.success('Visit assigned successfully!');
      setShowVisitModal(false);
      setVisitForm({
        staff_user_id: '', party_id: '', purpose: 'ORDER', priority: 'NORMAL',
        scheduled_date: new Date().toISOString().split('T')[0],
        scheduled_from_time: '', scheduled_to_time: '', notes: '',
      });
      fetchVisits();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to assign visit'));
    }
  };

  const handleCancelVisit = (visit) => {
    setConfirmModal({
      title: 'Cancel Visit',
      description: `Cancel the visit to ${visit.party_name} assigned to ${visit.staff_name}? This cannot be undone.`,
      confirmLabel: 'Cancel Visit',
      confirmClass: 'bg-rose-600 hover:bg-rose-700',
      icon: XCircle,
      iconBgClass: 'bg-rose-100',
      iconClass: 'text-rose-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.patch(`/part3/visits/${visit.id}/cancel`);
          toast.success('Visit cancelled');
          fetchVisits();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to cancel visit'));
        }
      },
    });
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      await api.patch(`/part3/tasks/${taskId}`, { status: newStatus });
      toast.success(`Task marked as ${newStatus === 'COMPLETED' ? 'done' : 'cancelled'}`);
      fetchTasks();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update task'));
    }
  };

  const handleDeleteTask = (task) => {
    setConfirmModal({
      title: 'Delete Task',
      description: `Permanently delete "${task.title}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      confirmClass: 'bg-rose-600 hover:bg-rose-700',
      icon: Trash2,
      iconBgClass: 'bg-rose-100',
      iconClass: 'text-rose-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.delete(`/part3/tasks/${task.id}`);
          toast.success('Task deleted');
          fetchTasks();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to delete task'));
        }
      },
    });
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getInitialColor = (name) => {
    const colors = [
      'bg-blue-100 text-blue-700',
      'bg-emerald-100 text-emerald-700',
      'bg-purple-100 text-purple-700',
      'bg-amber-100 text-amber-700',
      'bg-rose-100 text-rose-700',
      'bg-cyan-100 text-cyan-700',
    ];
    const index = (name?.charCodeAt(0) || 0) % colors.length;
    return colors[index];
  };

  const filteredVisits = visits.filter(visit =>
    visit.party_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    visit.staff_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredTasks = tasks.filter(task =>
    task.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    task.staff_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const completedVisits = filteredVisits.filter(v => v.status === 'COMPLETED').length;
  const pendingVisits = filteredVisits.filter(v => v.status !== 'COMPLETED').length;
  const completedTasks = filteredTasks.filter(t => t.status === 'COMPLETED').length;
  const pendingTasks = filteredTasks.filter(t => t.status !== 'COMPLETED').length;

  const getVisitStatusBadge = (status) => {
    const map = {
      PLANNED:     { label: 'Planned',     classes: 'bg-slate-100 text-slate-700',     Icon: Clock },
      IN_PROGRESS: { label: 'In Progress', classes: 'bg-blue-50 text-blue-700',        Icon: Clock },
      COMPLETED:   { label: 'Completed',   classes: 'bg-emerald-50 text-emerald-700',  Icon: CheckCircle },
      MISSED:      { label: 'Missed',      classes: 'bg-rose-50 text-rose-700',        Icon: AlertCircle },
      CANCELLED:   { label: 'Cancelled',   classes: 'bg-gray-100 text-gray-600',       Icon: XCircle },
    };
    const cfg = map[status] || { label: status || 'Unknown', classes: 'bg-amber-50 text-amber-700', Icon: Clock };
    const { label, classes, Icon } = cfg;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${classes}`}>
        <Icon className="w-3 h-3" />
        {label}
      </span>
    );
  };

  const getTaskStatusBadge = (status) => {
    const map = {
      COMPLETED: { label: 'Done',      cls: 'bg-emerald-50 text-emerald-700', Icon: CheckCircle },
      CANCELLED: { label: 'Cancelled', cls: 'bg-rose-50 text-rose-700',       Icon: XCircle },
    };
    const cfg = map[status] || { label: 'Pending', cls: 'bg-amber-50 text-amber-700', Icon: Clock };
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${cfg.cls}`}>
        <cfg.Icon className="w-3 h-3" />
        {cfg.label}
      </span>
    );
  };

  return (
    <div className="h-full flex flex-col bg-[#F7F8FA]">
      {/* Page Header */}
      <div className="flex-shrink-0 bg-[#F7F8FA] px-6 pt-5 pb-4">
        <div className="max-w-[1600px] mx-auto">
          {/* Title Row */}
          <div className="flex items-start justify-between mb-5">
            <div>
              <h1 className="text-xl font-semibold text-slate-800 tracking-tight">Visits & Tasks</h1>
              <p className="text-[13px] text-slate-500 mt-0.5 font-normal">Track staff visits and manage assigned tasks</p>
            </div>
            {activeTab === 'visits' ? (
              <button
                onClick={() => setShowVisitModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all text-[13px] font-medium shadow-sm"
              >
                <Plus className="w-4 h-4" strokeWidth={2} />
                Assign Visit
              </button>
            ) : (
              <button
                onClick={() => setShowTaskModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all text-[13px] font-medium shadow-sm"
              >
                <Plus className="w-4 h-4" strokeWidth={2} />
                Add Task
              </button>
            )}

            {/* Task Modal */}
            <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Create New Task</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateTask} className="space-y-4 pt-4">
                  <div>
                    <Label className="text-[13px] font-medium text-slate-700">Assign To *</Label>
                    <Select value={taskForm.staff_id} onValueChange={(value) => setTaskForm({ ...taskForm, staff_id: value })}>
                      <SelectTrigger className="mt-1.5">
                        <SelectValue placeholder="Select staff member" />
                      </SelectTrigger>
                      <SelectContent>
                        {staffList.map(staff => (
                          <SelectItem key={staff.id} value={staff.id}>{staff.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[13px] font-medium text-slate-700">Task Title *</Label>
                    <Input
                      value={taskForm.title}
                      onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                      placeholder="Enter task title"
                      className="mt-1.5"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-[13px] font-medium text-slate-700">Description</Label>
                    <textarea
                      value={taskForm.description}
                      onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                      placeholder="Enter task description"
                      className="w-full mt-1.5 px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[13px] font-medium text-slate-700">Due Date *</Label>
                      <Input
                        type="date"
                        value={taskForm.due_date}
                        onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                        className="mt-1.5"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-[13px] font-medium text-slate-700">Priority *</Label>
                      <Select value={taskForm.priority} onValueChange={(value) => setTaskForm({ ...taskForm, priority: value })}>
                        <SelectTrigger className="mt-1.5">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="LOW">Low</SelectItem>
                          <SelectItem value="NORMAL">Normal</SelectItem>
                          <SelectItem value="HIGH">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setShowTaskModal(false)}>Cancel</Button>
                    <Button type="submit" className="bg-teal-600 hover:bg-teal-700">Create Task</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            {/* Assign Visit Modal */}
            <Dialog open={showVisitModal} onOpenChange={setShowVisitModal}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Assign Visit</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateVisit} className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[13px] font-medium text-slate-700">Staff Member *</Label>
                      <Select value={visitForm.staff_user_id} onValueChange={(v) => setVisitForm({ ...visitForm, staff_user_id: v })}>
                        <SelectTrigger className="mt-1.5">
                          <SelectValue placeholder="Select staff" />
                        </SelectTrigger>
                        <SelectContent>
                          {staffList.map(s => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[13px] font-medium text-slate-700">Customer / Retailer *</Label>
                      <Select value={visitForm.party_id} onValueChange={(v) => setVisitForm({ ...visitForm, party_id: v })}>
                        <SelectTrigger className="mt-1.5">
                          <SelectValue placeholder="Select party" />
                        </SelectTrigger>
                        <SelectContent>
                          {parties.map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[13px] font-medium text-slate-700">Purpose *</Label>
                      <Select value={visitForm.purpose} onValueChange={(v) => setVisitForm({ ...visitForm, purpose: v })}>
                        <SelectTrigger className="mt-1.5">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ORDER">Order</SelectItem>
                          <SelectItem value="DELIVERY">Delivery</SelectItem>
                          <SelectItem value="COLLECTION">Collection</SelectItem>
                          <SelectItem value="CHECKUP">Check-up</SelectItem>
                          <SelectItem value="OTHER">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[13px] font-medium text-slate-700">Priority</Label>
                      <Select value={visitForm.priority} onValueChange={(v) => setVisitForm({ ...visitForm, priority: v })}>
                        <SelectTrigger className="mt-1.5">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="HIGH">High</SelectItem>
                          <SelectItem value="NORMAL">Normal</SelectItem>
                          <SelectItem value="LOW">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[13px] font-medium text-slate-700">Notes for Staff</Label>
                    <Input
                      value={visitForm.notes}
                      onChange={(e) => setVisitForm({ ...visitForm, notes: e.target.value })}
                      placeholder="e.g. Collect outstanding payment"
                      className="mt-1.5"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-[13px] font-medium text-slate-700">Scheduled Date</Label>
                      <Input
                        type="date"
                        value={visitForm.scheduled_date}
                        onChange={(e) => setVisitForm({ ...visitForm, scheduled_date: e.target.value })}
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label className="text-[13px] font-medium text-slate-700">From (optional)</Label>
                      <Input
                        type="time"
                        value={visitForm.scheduled_from_time}
                        onChange={(e) => setVisitForm({ ...visitForm, scheduled_from_time: e.target.value })}
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label className="text-[13px] font-medium text-slate-700">To (optional)</Label>
                      <Input
                        type="time"
                        value={visitForm.scheduled_to_time}
                        onChange={(e) => setVisitForm({ ...visitForm, scheduled_to_time: e.target.value })}
                        className="mt-1.5"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setShowVisitModal(false)}>Cancel</Button>
                    <Button
                      type="submit"
                      disabled={!visitForm.staff_user_id || !visitForm.party_id}
                      className="bg-teal-600 hover:bg-teal-700 disabled:opacity-50"
                    >
                      Assign Visit
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Stats Pills */}
          <div className="flex items-center gap-3 mb-5">
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-slate-50 flex items-center justify-center">
                <MapPin className="w-3.5 h-3.5 text-slate-500" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Total Visits</p>
                <p className="text-[14px] font-semibold text-slate-700">{filteredVisits.length}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-emerald-50 flex items-center justify-center">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Completed</p>
                <p className="text-[14px] font-semibold text-emerald-600">{completedVisits}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-amber-50 flex items-center justify-center">
                <Clock className="w-3.5 h-3.5 text-amber-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Pending</p>
                <p className="text-[14px] font-semibold text-amber-600">{pendingVisits}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-purple-50 flex items-center justify-center">
                <Target className="w-3.5 h-3.5 text-purple-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Tasks</p>
                <p className="text-[14px] font-semibold text-purple-600">{filteredTasks.length}</p>
              </div>
            </div>
          </div>

          {/* Tabs and Filters */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1 bg-white p-1 rounded-lg shadow-sm">
              <button
                onClick={() => setActiveTab('visits')}
                className={`px-4 py-1.5 rounded-md text-[13px] font-medium transition-all ${
                  activeTab === 'visits'
                    ? 'bg-teal-600 text-white'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                Visits
              </button>
              <button
                onClick={() => setActiveTab('tasks')}
                className={`px-4 py-1.5 rounded-md text-[13px] font-medium transition-all ${
                  activeTab === 'tasks'
                    ? 'bg-teal-600 text-white'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                Tasks
              </button>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-64 pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-[13px] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                />
              </div>

              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                />
              </div>

              <Select value={selectedStaff || "all"} onValueChange={(val) => setSelectedStaff(val === "all" ? "" : val)}>
                <SelectTrigger className="w-48 bg-white">
                  <SelectValue placeholder="All Staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {staffList.map(staff => (
                    <SelectItem key={staff.id} value={staff.id}>{staff.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto px-6 pb-6">
        <div className="max-w-[1600px] mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-10 h-10 border-3 border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
              </div>
            ) : activeTab === 'visits' ? (
              filteredVisits.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MapPin className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-800 mb-1">No visits found</h3>
                  <p className="text-[13px] text-slate-500">No staff visits recorded for this date</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left px-5 py-3 text-[11px] font-semibold text-teal-700 uppercase tracking-wider">Staff Member</th>
                      <th className="text-left px-5 py-3 text-[11px] font-semibold text-teal-700 uppercase tracking-wider">Retailer</th>
                      <th className="text-left px-5 py-3 text-[11px] font-semibold text-teal-700 uppercase tracking-wider">Check In</th>
                      <th className="text-left px-5 py-3 text-[11px] font-semibold text-teal-700 uppercase tracking-wider">Check Out</th>
                      <th className="text-left px-5 py-3 text-[11px] font-semibold text-teal-700 uppercase tracking-wider">Duration</th>
                      <th className="text-center px-5 py-3 text-[11px] font-semibold text-teal-700 uppercase tracking-wider">Status</th>
                      <th className="w-10 px-3 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVisits.map((visit, index) => (
                      <tr key={index} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-semibold ${getInitialColor(visit.staff_name)}`}>
                              {getInitials(visit.staff_name)}
                            </div>
                            <span className="text-[13px] font-medium text-slate-800">{visit.staff_name}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <Store className="w-4 h-4 text-slate-400" />
                            <span className="text-[13px] text-slate-700">{visit.party_name}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="text-[13px] text-slate-700">
                            {visit.checkin_time ? new Date(visit.checkin_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '—'}
                          </span>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="text-[13px] text-slate-700">
                            {visit.checkout_time ? new Date(visit.checkout_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '—'}
                          </span>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="text-[13px] text-slate-700">{visit.duration_minutes ? `${visit.duration_minutes} min` : '—'}</span>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          {getVisitStatusBadge(visit.status)}
                        </td>
                        <td className="px-3 py-3.5">
                          {['PLANNED', 'IN_PROGRESS'].includes(visit.status) ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="p-1.5 rounded-md hover:bg-slate-100 transition-colors">
                                  <MoreVertical className="w-4 h-4 text-slate-500" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-44">
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-rose-600 focus:text-rose-700 focus:bg-rose-50"
                                  onClick={() => handleCancelVisit(visit)}
                                >
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Cancel Visit
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : (
                            <div className="w-8" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            ) : (
              filteredTasks.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Target className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-800 mb-1">No tasks found</h3>
                  <p className="text-[13px] text-slate-500 mb-4">Create tasks to assign to your staff members</p>
                  <button
                    onClick={() => setShowTaskModal(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all text-[13px] font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    Add First Task
                  </button>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left px-5 py-3 text-[11px] font-semibold text-teal-700 uppercase tracking-wider">Task</th>
                      <th className="text-left px-5 py-3 text-[11px] font-semibold text-teal-700 uppercase tracking-wider">Assigned To</th>
                      <th className="text-left px-5 py-3 text-[11px] font-semibold text-teal-700 uppercase tracking-wider">Due Date</th>
                      <th className="text-center px-5 py-3 text-[11px] font-semibold text-teal-700 uppercase tracking-wider">Priority</th>
                      <th className="text-center px-5 py-3 text-[11px] font-semibold text-teal-700 uppercase tracking-wider">Status</th>
                      <th className="w-10 px-3 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTasks.map((task, index) => (
                      <tr key={index} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                        <td className="px-5 py-3.5">
                          <p className="text-[13px] font-medium text-slate-800">{task.title}</p>
                          {task.description && (
                            <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">{task.description}</p>
                          )}
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold ${getInitialColor(task.staff_name)}`}>
                              {getInitials(task.staff_name)}
                            </div>
                            <span className="text-[13px] text-slate-700">{task.staff_name}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-1.5 text-[13px] text-slate-600">
                            <Calendar className="w-3.5 h-3.5 text-slate-400" />
                            {task.due_date ? new Date(task.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${
                            task.priority === 'HIGH' ? 'bg-rose-50 text-rose-700' :
                            task.priority === 'NORMAL' ? 'bg-amber-50 text-amber-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {task.priority}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          {getTaskStatusBadge(task.status)}
                        </td>
                        <td className="px-3 py-3.5">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-1.5 rounded-md hover:bg-slate-100 transition-colors">
                                <MoreVertical className="w-4 h-4 text-slate-500" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              {task.status === 'PENDING' && (
                                <>
                                  <DropdownMenuItem onClick={() => handleUpdateTaskStatus(task.id, 'COMPLETED')}>
                                    <CheckCircle className="w-4 h-4 mr-2 text-emerald-600" />
                                    Mark Complete
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleUpdateTaskStatus(task.id, 'CANCELLED')}>
                                    <XCircle className="w-4 h-4 mr-2 text-slate-500" />
                                    Cancel Task
                                  </DropdownMenuItem>
                                </>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-rose-600 focus:text-rose-700 focus:bg-rose-50"
                                onClick={() => handleDeleteTask(task)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            )}
          </div>
        </div>
      </div>

      {/* Confirm Modal */}
      {confirmModal && (
        <ConfirmModal
          isOpen={!!confirmModal}
          title={confirmModal.title}
          description={confirmModal.description}
          confirmLabel={confirmModal.confirmLabel}
          confirmClass={confirmModal.confirmClass}
          icon={confirmModal.icon}
          iconBgClass={confirmModal.iconBgClass}
          iconClass={confirmModal.iconClass}
          onConfirm={confirmModal.onConfirm}
          onClose={() => setConfirmModal(null)}
        />
      )}
    </div>
  );
};

export default VisitsTasksPage;
